import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useTransactions, useSettings, useGoals, useBankBalances, addToCollection, updateInCollection, deleteFromCollection } from '../hooks/useFirestore';

const FinanceContext = createContext();

export const useFinance = () => useContext(FinanceContext);

export const FinanceProvider = ({ children }) => {
    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    );

    const { data: transactions, loading: txLoading } = useTransactions(selectedMonth);
    const { settings, loading: settingsLoading, updateSettings } = useSettings();
    const { data: goals, loading: goalsLoading } = useGoals();
    const { data: bankBalances } = useBankBalances();

    const isNewMonth = now.getDate() === 1;

    // Compute stats from real transactions + bank balances
    const stats = useMemo(() => {
        const bankBalance = (bankBalances || []).reduce((s, b) => s + (Number(b.balance) || 0), 0);

        if (!transactions || transactions.length === 0) {
            return {
                income: 0, expenses: 0, profit: 0, dailyTargetIncome: 0,
                dailySpendCap: 0, dailyBudget: 0, todaySpend: 0, sipSuggestion: 0,
                totalSaved: 0, savingsRate: 0, transactionCount: 0,
                categoryBreakdown: {}, dailySpend: {},
                bankBalance, dailyBudgetRemaining: 0, cashRunwayDays: 0,
            };
        }

        const today = new Date();
        const [year, month] = selectedMonth.split('-').map(Number);
        const daysInMonth = new Date(year, month, 0).getDate();
        const daysPassed = (year === today.getFullYear() && month === today.getMonth() + 1)
            ? today.getDate() : daysInMonth;

        let income = 0, expenses = 0;
        const categoryBreakdown = {};
        const dailySpend = {};
        let todaySpend = 0;

        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        transactions.forEach(t => {
            const amt = Math.abs(t.amount || 0);
            const isCredit = t.type === 'credit' || t.type === 'income' || t.type === 'gain';

            if (isCredit) {
                income += amt;
            } else {
                expenses += amt;
                // Category breakdown
                const cat = t.category || 'Other';
                categoryBreakdown[cat] = (categoryBreakdown[cat] || 0) + amt;
                // Daily spend map (use local date to avoid UTC day-shift)
                const txDate = new Date(t.date);
                const day = `${txDate.getFullYear()}-${String(txDate.getMonth() + 1).padStart(2, '0')}-${String(txDate.getDate()).padStart(2, '0')}`;
                if (day) dailySpend[day] = (dailySpend[day] || 0) + amt;
                // Today's spend
                if (day === todayStr) todaySpend += amt;
            }
        });

        const profit = income - expenses;
        const monthlySpendCap = settings?.shoppingCap ? (settings.shoppingCap + (settings.lifestyleCap || 0)) : 0;
        const dailySpendCap = monthlySpendCap > 0 ? monthlySpendCap / daysInMonth : 0;
        const dailyBudget = dailySpendCap;
        const dailyTargetIncome = settings?.monthlyIncomeTarget
            ? Math.max(0, (settings.monthlyIncomeTarget - income) / Math.max(1, daysInMonth - daysPassed + 1))
            : 0;
        const sipSuggestion = todaySpend < dailySpendCap ? (dailySpendCap - todaySpend) : 0;
        const savingsRate = income > 0 ? ((profit / income) * 100) : 0;

        // Financial intelligence: daily budget remaining & cash runway
        const dailyBudgetRemaining = income > 0
            ? (income * 0.7 / daysInMonth) - todaySpend
            : 0;
        const dailyBurnRate = daysPassed > 0 ? expenses / daysPassed : 0;
        const cashRunwayDays = dailyBurnRate > 0
            ? Math.round(bankBalance / dailyBurnRate)
            : bankBalance > 0 ? 999 : 0;

        return {
            income, expenses, profit, dailyTargetIncome, dailySpendCap,
            dailyBudget, todaySpend, sipSuggestion,
            totalSaved: profit,
            savingsRate, transactionCount: transactions.length,
            categoryBreakdown, dailySpend,
            bankBalance, dailyBudgetRemaining, cashRunwayDays,
        };
    }, [transactions, selectedMonth, settings, bankBalances]);

    // Backward-compat getStats
    const getStats = useCallback(() => stats, [stats]);

    // Targets derived from settings
    const targets = useMemo(() => ({
        monthlyIncome: settings?.monthlyIncomeTarget || 0,
        monthlySpendCap: (settings?.shoppingCap || 0) + (settings?.lifestyleCap || 0),
        monthlySavingsGoal: settings?.savingsTarget || 0,
        lastMonthPerformance: settings?.lastMonthPerformance || { income: 0, spend: 0, savings: 0 },
    }), [settings]);

    const updateTargets = useCallback(async (newTargets) => {
        await updateSettings({
            monthlyIncomeTarget: newTargets.monthlyIncome,
            savingsTarget: newTargets.monthlySavingsGoal,
            shoppingCap: newTargets.monthlySpendCap,
        });
    }, [updateSettings]);

    // Goals CRUD
    const addGoal = useCallback(async (goal) => {
        return addToCollection('goals', goal);
    }, []);

    const updateGoal = useCallback(async (id, data) => {
        return updateInCollection('goals', id, data);
    }, []);

    const deleteGoal = useCallback(async (id) => {
        return deleteFromCollection('goals', id);
    }, []);

    // Add manual transaction
    const addTransaction = useCallback(async (t) => {
        return addToCollection('transactions', {
            ...t,
            date: t.date || new Date().toISOString(),
        });
    }, []);

    return (
        <FinanceContext.Provider value={{
            transactions,
            goals,
            targets,
            settings,
            selectedMonth,
            setSelectedMonth,
            isNewMonth,
            stats,
            getStats,
            addTransaction,
            updateTargets,
            updateSettings,
            addGoal,
            updateGoal,
            deleteGoal,
            loading: txLoading || settingsLoading || goalsLoading,
        }}>
            {children}
        </FinanceContext.Provider>
    );
};
